import { useCallback } from 'react';
import { MatchScoresResponse, UUID } from '@/api/generatedApi';
import { SelectionSyncSide } from '@physna/core/domain/viewer/selection-sync-messaging';
import { useGetAsset } from '@/hooks/api/assets/useGetAsset';
import { Box } from '@mui/material';
import { AssetActionRow } from '../AssetActionRow';
import { LeftPanel } from '../DraggablePanel/LeftPanel';
import { AssetDetails } from './AssetDetails';
import { AssetMetadata } from './AssetMetadata';
import { DependencyTreeDetails } from './DependencyTree/DependencyTreeDetails';
import { DependencyContext } from './DependencyTree/DependencyTreeView';
import { ComparePageObjectModel } from '@/poms/comparePageObjectModel';
import { MatchPercentageDisplay } from './MatchPercentageDisplay';
import { BodyText } from '@/components/BodyText';
import { HelpIcon } from '../HelpContent/HelpIcon';
import CompareIcon from '@mui/icons-material/Compare';
import { SelectionPathsBySide } from '@/hooks/compare/useCompareSelection';

export const CompareSidebar = ({
  asset1Id,
  asset2Id,
  asset1TenantId,
  asset2TenantId,
  onWidthChange,
  geometricScores,
  volumetricScores,
  selectionPaths,
  onTreeItemClick,
}: {
  asset1Id?: UUID;
  asset2Id?: UUID;
  asset1TenantId?: UUID;
  asset2TenantId?: UUID;
  onWidthChange?: (width: number) => void;
  geometricScores?: MatchScoresResponse['geometric'];
  volumetricScores?: MatchScoresResponse['volumetric'];
  selectionPaths?: SelectionPathsBySide;
  onTreeItemClick?: (side: SelectionSyncSide, assetId: string, selectionPath?: string[]) => void;
}) => {
  const { asset: asset1 } = useGetAsset({ assetId: asset1Id, assetTenantId: asset1TenantId });
  const { asset: asset2 } = useGetAsset({ assetId: asset2Id, assetTenantId: asset2TenantId ?? asset1TenantId });

  const handleAsset1TreeItemClick = useCallback(
    (assetId: string, _context?: DependencyContext, selectionPath?: string[]) =>
      onTreeItemClick?.('asset1', assetId, selectionPath),
    [onTreeItemClick]
  );

  const handleAsset2TreeItemClick = useCallback(
    (assetId: string, _context?: DependencyContext, selectionPath?: string[]) =>
      onTreeItemClick?.('asset2', assetId, selectionPath),
    [onTreeItemClick]
  );

  return (
    <LeftPanel
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
      onWidthChange={onWidthChange}
      data-testid={ComparePageObjectModel.CompareSidebar}
    >
      <MatchPercentageDisplay
        geometricScores={geometricScores}
        volumetricScores={volumetricScores}
        asset1={asset1}
        asset2={asset2}
      />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <></>
        {asset1 ? (
          <>
            <AssetActionRow asset={asset1} />
            <Box sx={{ height: 'calc(100% - 56px)', overflowY: 'auto' }}>
              <AssetDetails asset={asset1} />
              <DependencyTreeDetails
                asset={asset1}
                readOnly
                selectedSelectionPath={selectionPaths?.asset1}
                onTreeItemClick={onTreeItemClick && handleAsset1TreeItemClick}
              />
              <AssetMetadata asset={asset1} />
            </Box>
          </>
        ) : (
          <EmptyCompare />
        )}
      </Box>
      <Box aria-hidden sx={{ height: '4px', flexShrink: 0, backgroundColor: 'secondary.main' }} />
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {asset2 ? (
          <>
            <AssetActionRow asset={asset2} />
            <Box sx={{ height: 'calc(100% - 56px)', overflowY: 'auto' }}>
              <AssetDetails asset={asset2} />
              <DependencyTreeDetails
                asset={asset2}
                readOnly
                selectedSelectionPath={selectionPaths?.asset2}
                onTreeItemClick={onTreeItemClick && handleAsset2TreeItemClick}
              />
              <AssetMetadata asset={asset2} />
            </Box>
          </>
        ) : (
          <EmptyCompare />
        )}
      </Box>
    </LeftPanel>
  );
};

const EmptyCompare = () => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '24px' }}>
      <BodyText component='p' sx={{ textAlign: 'center', color: 'text.secondary' }}>
        Click{' '}
        <HelpIcon>
          <CompareIcon sx={{ fontSize: '16px' }} />
        </HelpIcon>{' '}
        to compare
      </BodyText>
    </Box>
  );
};
